import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";

import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {PageLifecycle} from "@khan/analytics-performance";
import {ExtraInfoLayout, RolePageHeroFigure} from "@khan/auth-shared";
import {mediaQueries} from "@khan/shared-styles";

import PageRoot from "#app-shell-package/page-root.tsx";
import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";

import {RolePicker} from "./role-picker.tsx";
import useBypassRolePicker from "./use-bypass-role-picker.tsx";

/**
 * Page for choosing a primary role (Learner, Teacher, or Parent) during sign
 * up.
 */
const ChooseRolePage = () => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    // NOTE: This component is currently only used on /signup. If that changes,
    // we will need to update these mount effects.
    useOnMountEffect(() => {
        PageLifecycle.reportPageLifecycleTiming("signup_page", {
            interactive: true,
            usable: true,
        });

        publishAnalyticsEvent(
            {
                eventType: "SignupPageViewed",
                eventSchemaVersion: 1,
            },
            "pageview_signup", // legacy bingo conversion id
        );
    });

    const learnerNextUrl = "/signup/learner/birthday";
    const teacherNextUrl = "/signup/teacher";
    const parentNextUrl = "/signup/parent";

    const bypassResult = useBypassRolePicker();
    if (bypassResult.status === "success" && bypassResult.role) {
        if (bypassResult.role === "learner") {
            return <SmartRedirect to={learnerNextUrl} preserveSearchParams />;
        }
        if (bypassResult.role === "teacher") {
            return <SmartRedirect to={teacherNextUrl} preserveSearchParams />;
        }
        if (bypassResult.role === "parent") {
            return <SmartRedirect to={parentNextUrl} preserveSearchParams />;
        }
    }

    return (
        <PageRoot settings={{footer: false}}>
            <View style={styles.page}>
                <ExtraInfoLayout
                    leftComponent={<RolePageHeroFigure />}
                    rightComponent={
                        <RolePicker
                            title={t`Sign up for Khan Academy!`}
                            learnerNextUrl={learnerNextUrl}
                            teacherNextUrl={teacherNextUrl}
                            parentNextUrl={parentNextUrl}
                        />
                    }
                    columnTemplate="60% 40%"
                />
            </View>
        </PageRoot>
    );
};

const styles = StyleSheet.create({
    page: {
        alignItems: "center",
        [mediaQueries.lgOrLarger]: {
            padding: `${spacing.xxxLarge_64}px 0`,
        },
    },
});

export default ChooseRolePage;
